import React, { useState } from 'react';
import '../styles/AddItemsModal.css';

interface ItemWithSize {
  name: string;
  size: string;
}

interface AddItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (items: string[], customItems?: string[]) => void;
  onAddWithSizes?: (items: ItemWithSize[], customItems?: string[]) => void;
  availableOptions: string[];
  title: string;
  customInputPlaceholder?: string;
  emptyMessage?: string;
  showSizeInput?: boolean;
  sizePlaceholder?: string;
}

const AddItemsModal: React.FC<AddItemsModalProps> = ({
  isOpen,
  onClose,
  onAdd,
  onAddWithSizes,
  availableOptions,
  title,
  customInputPlaceholder = 'Enter custom item...',
  emptyMessage = 'No options available. Add a custom item below.',
  showSizeInput = false,
  sizePlaceholder = 'Size'
}) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [itemSizes, setItemSizes] = useState<Record<string, string>>({});
  const [customItem, setCustomItem] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  if (!isOpen) return null;

  const handleToggle = (option: string) => {
    setSelectedOptions(prev => {
      if (prev.includes(option)) {
        // Remove from sizes when unchecked
        const newSizes = { ...itemSizes };
        delete newSizes[option];
        setItemSizes(newSizes);
        return prev.filter(o => o !== option);
      }
      return [...prev, option];
    });
  };

  const handleSizeChange = (option: string, size: string) => {
    setItemSizes(prev => ({ ...prev, [option]: size }));
  };

  const handleAddCustom = () => {
    const trimmed = customItem.trim();
    if (trimmed && !selectedOptions.includes(trimmed)) {
      setSelectedOptions(prev => [...prev, trimmed]);
      setCustomItem('');
      setShowCustomInput(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddCustom();
    }
  };

  const handleSubmit = () => {
    if (selectedOptions.length > 0) {
      // Separate custom items (ones not in availableOptions) from selected options
      const customItems = selectedOptions.filter(item => !availableOptions.includes(item));

      if (showSizeInput && onAddWithSizes) {
        // Return items with sizes
        const itemsWithSizes: ItemWithSize[] = selectedOptions.map(item => ({
          name: item,
          size: itemSizes[item] || ''
        }));
        onAddWithSizes(itemsWithSizes, customItems);
      } else {
        onAdd(selectedOptions, customItems);
      }

      setSelectedOptions([]);
      setItemSizes({});
      setCustomItem('');
      setShowCustomInput(false);
      onClose();
    }
  };

  const handleCancel = () => {
    setSelectedOptions([]);
    setItemSizes({});
    setCustomItem('');
    setShowCustomInput(false);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={handleCancel}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={handleCancel}>✕</button>
        </div>

        <div className="modal-body">
          {/* Available options grid */}
          {availableOptions.length > 0 ? (
            <div className="add-items-options-grid">
              {availableOptions.map(option => (
                <label key={option} className="add-items-option">
                  <input
                    type="checkbox"
                    checked={selectedOptions.includes(option)}
                    onChange={() => handleToggle(option)}
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          ) : (
            <p style={{ color: '#9CA3AF', fontStyle: 'italic', marginBottom: '16px' }}>
              {emptyMessage}
            </p>
          )}

          {/* Custom input section */}
          <div className="add-items-custom-section">
            {!showCustomInput ? (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setShowCustomInput(true)}
              >
                + Add Custom Item
              </button>
            ) : (
              <div className="add-items-custom-input">
                <input
                  type="text"
                  className="form-input"
                  value={customItem}
                  onChange={(e) => setCustomItem(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={customInputPlaceholder}
                  autoFocus
                />
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={handleAddCustom}
                  disabled={!customItem.trim()}
                >
                  Add
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setShowCustomInput(false);
                    setCustomItem('');
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {/* Selected items preview with optional size input */}
          {selectedOptions.length > 0 && (
            <div className="add-items-selected">
              <div className="add-items-selected-header">
                Selected ({selectedOptions.length}):
              </div>
              <div className="add-items-selected-list">
                {selectedOptions.map(item => (
                  <div key={item} className="add-items-selected-item" style={showSizeInput ? { display: 'flex', alignItems: 'center', gap: '8px' } : undefined}>
                    <span style={showSizeInput ? { flex: 1 } : undefined}>{item}</span>
                    {showSizeInput && (
                      <input
                        type="number"
                        className="form-input"
                        placeholder={sizePlaceholder}
                        value={itemSizes[item] || ''}
                        onChange={(e) => handleSizeChange(item, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ width: '80px', padding: '4px 8px', fontSize: '12px' }}
                      />
                    )}
                    <button
                      onClick={() => setSelectedOptions(prev => prev.filter(i => i !== item))}
                      className="remove-btn"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary btn-md"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary btn-md"
            onClick={handleSubmit}
            disabled={selectedOptions.length === 0}
          >
            Add Selected ({selectedOptions.length})
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddItemsModal;
